
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// Components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// Types
import { BillingDiscountT, BillingDiscountPayloadT } from '@/core/types/billing.types'
import { CompanyT } from '@/core/types/companies.types'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Realisations
import { companies } from '@/core/realisations/companies.realisation'
import { billing } from '@/core/realisations/billing.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, minValue, maxValue, numeric } from '@vuelidate/validators'

// Constants
import { DISCOUNT_ENTITY_TYPES, DISCOUNT_UNIT_TYPES } from '@/core/constants/billing.constants'
import { useDebounce } from 'vue-composable'

export default defineComponent({
    components: {
        Modal,
        TheSelect,
        TheInput,
        Loader,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: [Object, null] as PropType<BillingDiscountT | null>,
            default: null,
        },
    },
    emits: ['create', 'cancel'],
    setup(props, context) {
        const state = reactive({
            entityType: null as string | null,
            unitId: null as number | null,
            invoice_number: null as string | null,
            is_calculation_loading: false,
            calculation_id: null as number | null,
            company_id: null as number | null,
            cost_amount: null as number | null,
            percent_amount: null as number | null,
            expiredAt: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                entityType: { required },
                unitId: { required },
                invoice_number: {
                    required: requiredIf(() => state.entityType === DISCOUNT_ENTITY_TYPES[0].id),
                    invoice_finded: () => {
                        if (
                            !state.calculation_id &&
                            state.entityType === DISCOUNT_ENTITY_TYPES[0].id
                        )
                            return false
                        return true
                    },
                },
                company_id: {
                    required: requiredIf(() => state.entityType === DISCOUNT_ENTITY_TYPES[1].id),
                },
                cost_amount: {
                    required: requiredIf(() => state.unitId === DISCOUNT_UNIT_TYPES[0].id),
                    numeric,
                    minValue: minValue(1),
                },
                percent_amount: {
                    required: requiredIf(() => state.unitId === DISCOUNT_UNIT_TYPES[1].id),
                    numeric,
                    maxValue: maxValue(100),
                    minValue: minValue(1),
                },
            },
            state
        )

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({ page, search, statusId: 2 })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const onEntityTypeChange = (type: string) => {
            if (type === DISCOUNT_ENTITY_TYPES[1].id) {
                loadCompanies(1)
            }
        }

        const searchCalculation = async () => {
            state.calculation_id = null
            if (!state.invoice_number) return

            try {
                state.is_calculation_loading = true

                const response = await billing.getCalculations({
                    invoiceNumber: state.invoice_number as string,
                })

                if (response.data.length === 1) {
                    state.calculation_id = response.data[0].id
                }
            } catch (error) {
                console.error(error)
            } finally {
                state.is_calculation_loading = false
                state_form$.value.invoice_number.$touch()
            }
        }

        const debouncedSearch = useDebounce(searchCalculation, 1000)

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление скидки'
            if (props.isUpdate) return 'Редактирование скидки'
            if (props.isDelete) return 'Удаление скидки'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            let entityId = 0
            let amount = 0

            if (state.entityType === DISCOUNT_ENTITY_TYPES[0].id) {
                entityId = state.calculation_id as number
            }

            if (state.entityType === DISCOUNT_ENTITY_TYPES[1].id) {
                entityId = state.company_id as number
            }

            if (state.unitId === DISCOUNT_UNIT_TYPES[0].id) {
                amount = Number(state.cost_amount)
            }

            if (state.unitId === DISCOUNT_UNIT_TYPES[1].id) {
                amount = Number(state.percent_amount)
            }

            const payload: BillingDiscountPayloadT = {
                entityType: state.entityType as string,
                entityId: entityId,
                unitId: state.unitId as number,
                amount,
                expiredAt: state.expiredAt,
            }

            context.emit('create', payload)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
        }

        const emitCancel = () => {
            context.emit('cancel')
        }

        const min_date = computed(() => {
            const date = new Date(Date.now())
            return date.toISOString().split('T')[0]
        })

        return {
            state,
            state_form$,
            data_state,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            DISCOUNT_ENTITY_TYPES,
            DISCOUNT_UNIT_TYPES,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            onEntityTypeChange,
            min_date,
            debouncedSearch,
        }
    },
})
